import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';
import type { TimeRangeOption } from '@/types/analytics';

export { type TimeRangeOption };

export interface AffiliateClick {
  id: string;
  partner_name: string;
  category: string | null;
  clicks: number;
  previous_clicks?: number;
  growth?: number;
}

export interface CategoryClick {
  category: string;
  clicks: number;
  previous_clicks?: number;
  growth?: number;
}

export interface PageClick {
  page: string;
  clicks: number;
  conversions: number;
  conversion_rate: number;
}

export interface DateClick {
  date: string;
  clicks: number;
  previous_clicks?: number;
  growth?: number;
}

export const useAffiliateAnalytics = (initialTimeRange: TimeRangeOption = '7days') => {
  const [timeRange, setTimeRange] = useState<TimeRangeOption>(initialTimeRange);
  const [loading, setLoading] = useState(true);
  const [clicksByDate, setClicksByDate] = useState<DateClick[]>([]);
  const [clicksByCategory, setClicksByCategory] = useState<CategoryClick[]>([]);
  const [clicksByPartner, setClicksByPartner] = useState<AffiliateClick[]>([]);
  const [clicksByPage, setClicksByPage] = useState<PageClick[]>([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [previousTotalClicks, setPreviousTotalClicks] = useState(0);
  const [clicksGrowth, setClicksGrowth] = useState(0);

  const getDateRange = (range: TimeRangeOption) => {
    const now = new Date();
    let days: number;
    
    switch (range) {
      case '24h':
        days = 1;
        break;
      case '7days':
        days = 7;
        break;
      case '30days':
        days = 30;
        break;
      case '90days':
        days = 90;
        break;
      default:
        days = 7;
    }
    
    return {
      startDate: subDays(now, days),
      endDate: now,
      previousStartDate: subDays(now, days * 2),
      previousEndDate: subDays(now, days)
    };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { startDate, endDate, previousStartDate, previousEndDate } = getDateRange(timeRange);

      // Fetch current period analytics
      const { data: currentData, error: currentError } = await supabase
        .from('affiliate_clicks')
        .select('*')
        .gte('clicked_at', startDate.toISOString())
        .lte('clicked_at', endDate.toISOString());

      if (currentError) {
        console.error("Error fetching current affiliate analytics:", currentError);
        setLoading(false);
        return;
      }

      // Fetch previous period analytics
      const { data: previousData, error: previousError } = await supabase
        .from('affiliate_clicks')
        .select('*')
        .gte('clicked_at', previousStartDate.toISOString())
        .lte('clicked_at', previousEndDate.toISOString());

      if (previousError) {
        console.error("Error fetching previous affiliate analytics:", previousError);
        setLoading(false);
        return;
      }

      // Process current period data
      const dailyClicksMap: { [date: string]: number } = {};
      const categoryClicksMap: { [category: string]: number } = {};
      const partnerClicksMap: { [partnerName: string]: { id: string, partner_name: string, category: string | null, clicks: number } } = {};
      const pageClicksMap: { [page: string]: number } = {};

      let currentTotalClicks = 0;

      currentData?.forEach(click => {
        const formattedDate = format(new Date(click.clicked_at), 'yyyy-MM-dd');
        dailyClicksMap[formattedDate] = (dailyClicksMap[formattedDate] || 0) + 1;

        if (click.category) {
          categoryClicksMap[click.category] = (categoryClicksMap[click.category] || 0) + 1;
        }

        // Extract page from referrer
        if (click.referrer) {
          try {
            const url = new URL(click.referrer);
            const page = url.pathname || '/';
            pageClicksMap[page] = (pageClicksMap[page] || 0) + 1;
          } catch {
            pageClicksMap['/'] = (pageClicksMap['/'] || 0) + 1;
          }
        }

        currentTotalClicks++;

        if (partnerClicksMap[click.partner_name]) {
          partnerClicksMap[click.partner_name].clicks++;
        } else {
          partnerClicksMap[click.partner_name] = {
            id: click.id,
            partner_name: click.partner_name,
            category: click.category,
            clicks: 1
          };
        }
      });

      // Process previous period data to calculate growth
      let prevTotalClicks = 0;
      previousData?.forEach(() => {
        prevTotalClicks++;
      });

      const dailyClicks: DateClick[] = Object.keys(dailyClicksMap).map(date => ({
        date,
        clicks: dailyClicksMap[date]
      })).sort((a, b) => a.date.localeCompare(b.date));

      const categoryClicks: CategoryClick[] = Object.keys(categoryClicksMap).map(category => ({
        category,
        clicks: categoryClicksMap[category]
      })).sort((a, b) => b.clicks - a.clicks);

      const partnerClicks: AffiliateClick[] = Object.values(partnerClicksMap).sort((a, b) => b.clicks - a.clicks);

      const pageClicks: PageClick[] = Object.keys(pageClicksMap).map(page => ({
        page,
        clicks: pageClicksMap[page],
        conversions: 0, // Will need to implement conversion tracking
        conversion_rate: 0
      })).sort((a, b) => b.clicks - a.clicks);

      // Calculate click growth
      const growth = prevTotalClicks === 0 ? 100 : ((currentTotalClicks - prevTotalClicks) / prevTotalClicks) * 100;

      setClicksByDate(dailyClicks);
      setClicksByCategory(categoryClicks);
      setClicksByPartner(partnerClicks);
      setClicksByPage(pageClicks);
      setTotalClicks(currentTotalClicks);
      setPreviousTotalClicks(prevTotalClicks);
      setClicksGrowth(growth);

    } catch (error) {
      console.error("Affiliate analytics fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  return {
    loading,
    clicksByDate,
    clicksByCategory,
    clicksByPartner,
    clicksByPage,
    totalClicks,
    previousTotalClicks,
    clicksGrowth,
    timeRange,
    setTimeRange
  };
};
